import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DisplayLayer,
  DisplayLayerKind,
  DisplayTemplateBindingTarget,
  DisplayTemplateDocument,
  DisplayTemplateItem,
  DisplayTemplateRevision,
  DisplayGameSettingDefinition,
  GameVariantItem,
} from "@shared/index";
import { api, ApiRequestError } from "@/lib/api";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  Modal,
  Select,
} from "@/components/ui";
import { copy, useI18n } from "@/lib/i18n";
import { useToast } from "@/lib/toast";

type EditorProduct = { id: string; name: string; category: string };
type EditorEndpoint = { id: string; name: string };
type MediaItem = {
  id: string;
  url: string;
  name: string;
  kind: string;
  posterUrl?: string | null;
};
type Interaction = {
  mode: "move" | "resize";
  id: string;
  startX: number;
  startY: number;
  origin: DisplayLayer;
};

const LAYER_LIBRARY: Array<{
  kind: DisplayLayerKind;
  tr: string;
  en: string;
  width: number;
  height: number;
}> = [
  { kind: "game", tr: "Oyun alanı", en: "Game area", width: 70, height: 70 },
  { kind: "qr", tr: "Katılım QR", en: "Join QR", width: 20, height: 32 },
  { kind: "players", tr: "Oyuncular", en: "Players", width: 48, height: 18 },
  { kind: "score", tr: "Skor", en: "Score", width: 18, height: 12 },
  { kind: "timer", tr: "Sayaç", en: "Timer", width: 14, height: 10 },
  { kind: "logo", tr: "Logo", en: "Logo", width: 18, height: 10 },
  { kind: "lobby", tr: "Lobi", en: "Lobby", width: 70, height: 65 },
  { kind: "results", tr: "Sonuçlar", en: "Results", width: 64, height: 58 },
  { kind: "voting", tr: "Oylama", en: "Voting", width: 70, height: 58 },
  { kind: "ad", tr: "Reklam", en: "Advertisement", width: 88, height: 12 },
  { kind: "sponsor", tr: "Sponsor", en: "Sponsor", width: 30, height: 14 },
  { kind: "branding", tr: "Marka alanı", en: "Branding", width: 22, height: 9 },
];

const PRESETS = [
  { id: "desktop", tr: "Masaüstü", en: "Desktop", width: 1920, height: 1080 },
  { id: "led", tr: "4K LED", en: "4K LED", width: 3840, height: 2160 },
  {
    id: "wide",
    tr: "Ultra geniş",
    en: "Ultra wide",
    width: 3840,
    height: 1080,
  },
  { id: "portrait", tr: "Dikey", en: "Portrait", width: 1080, height: 1920 },
];

function message(error: unknown, fallback: string) {
  return error instanceof ApiRequestError ? error.message : fallback;
}
function cloneDocument(
  value: DisplayTemplateDocument,
): DisplayTemplateDocument {
  return JSON.parse(JSON.stringify(value)) as DisplayTemplateDocument;
}
function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
function round(value: number, snap: boolean) {
  return Number((snap ? Math.round(value) : value).toFixed(2));
}
function layerLabel(
  kind: DisplayLayerKind,
  c: (tr: string, en: string) => string,
) {
  const definition = LAYER_LIBRARY.find((item) => item.kind === kind);
  return c(definition?.tr ?? kind, definition?.en ?? kind);
}
function nextLayerId(kind: DisplayLayerKind, layers: DisplayLayer[]) {
  let index = 1;
  let id = `${kind}-${index}`;
  while (layers.some((layer) => layer.id === id)) id = `${kind}-${++index}`;
  return id;
}
function sampleForLayer(
  layer: DisplayLayer,
  c: (tr: string, en: string) => string,
) {
  const settings = layer.settings as Record<string, unknown>;
  if (
    (layer.kind === "logo" ||
      layer.kind === "sponsor" ||
      layer.kind === "ad") &&
    typeof settings.mediaUrl === "string"
  )
    return settings.mediaKind === "video" ? (
      <video
        src={settings.mediaUrl}
        poster={
          typeof settings.posterUrl === "string"
            ? settings.posterUrl
            : undefined
        }
        muted
      />
    ) : (
      <img src={settings.mediaUrl} alt="" />
    );
  if (layer.kind === "game")
    return (
      <>
        <span className="template-editor__game-glow" />
        <strong>{c("İZOLE OYUN ALANI", "ISOLATED GAME AREA")}</strong>
        <small>
          {c(
            "Oyun build’i bu sınır içinde çalışır",
            "The game build runs inside this boundary",
          )}
        </small>
      </>
    );
  if (layer.kind === "qr")
    return (
      <>
        <span className="template-editor__fake-qr" aria-hidden="true" />
        <strong>{c("TELEFONUNLA KATIL", "JOIN WITH YOUR PHONE")}</strong>
      </>
    );
  if (layer.kind === "players")
    return (
      <>
        <span className="template-editor__avatars">
          <i />
          <i />
          <i />
          <i />
        </span>
        <strong>{c("OYUNCULAR", "PLAYERS")}</strong>
      </>
    );
  if (layer.kind === "score")
    return (
      <>
        <small>{c("SKOR", "SCORE")}</small>
        <strong>{String(settings.sampleValue ?? "2.450")}</strong>
      </>
    );
  if (layer.kind === "timer")
    return (
      <>
        <small>{c("KALAN", "LEFT")}</small>
        <strong>{String(settings.sampleValue ?? "00:42")}</strong>
      </>
    );
  if (layer.kind === "lobby")
    return (
      <>
        <small>{c("OYUNCULAR HAZIRLANIYOR", "PLAYERS GETTING READY")}</small>
        <strong>{c("SIRADAKİ RAUNT", "NEXT ROUND")}</strong>
      </>
    );
  if (layer.kind === "results")
    return (
      <>
        <small>{c("TUR SONUCU", "ROUND RESULT")}</small>
        <strong>1 · 2 · 3</strong>
      </>
    );
  if (layer.kind === "voting")
    return (
      <>
        <small>{c("SIRADAKİ OYUN", "NEXT GAME")}</small>
        <strong>{c("SEÇİMİNİ YAP", "CAST YOUR VOTE")}</strong>
      </>
    );
  if (layer.kind === "branding" || layer.kind === "logo")
    return <strong>WONDEON</strong>;
  if (layer.kind === "ad" || layer.kind === "sponsor")
    return (
      <>
        <small>{c("ONAYLI İÇERİK", "APPROVED CONTENT")}</small>
        <strong>{c("Sponsor alanı", "Sponsor area")}</strong>
      </>
    );
  return <strong>{layerLabel(layer.kind, c)}</strong>;
}

export function DisplayTemplateEditor({
  template,
  accountId,
  products,
  endpoints,
  onClose,
  onSaved,
}: {
  template: DisplayTemplateItem;
  accountId?: string;
  products: EditorProduct[];
  endpoints: EditorEndpoint[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { language } = useI18n();
  const c = useCallback(
    (tr: string, en: string) => copy(language, tr, en),
    [language],
  );
  const toast = useToast();
  const canvasRef = useRef<HTMLDivElement>(null);
  const [document, setDocument] = useState<DisplayTemplateDocument>(() =>
    cloneDocument(template.document),
  );
  const [selectedId, setSelectedId] = useState(
    template.document.layers.find((layer) => layer.kind !== "game")?.id ??
      template.document.layers[0]?.id ??
      "",
  );
  const [past, setPast] = useState<DisplayTemplateDocument[]>([]);
  const [future, setFuture] = useState<DisplayTemplateDocument[]>([]);
  const [interaction, setInteraction] = useState<Interaction | null>(null);
  const [snap, setSnap] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [preview, setPreview] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [bindingType, setBindingType] =
    useState<DisplayTemplateBindingTarget>("account");
  const [bindingKey, setBindingKey] = useState(template.accountId);

  useEffect(() => {
    setDocument(cloneDocument(template.document));
    setPast([]);
    setFuture([]);
    setSelectedId(
      template.document.layers.find((layer) => layer.kind !== "game")?.id ??
        template.document.layers[0]?.id ??
        "",
    );
  }, [template]);

  const selected =
    document.layers.find((layer) => layer.id === selectedId) ?? null;
  const hasUnpublishedRevision =
    template.publishedRevision !== template.revision;
  const query = accountId ? { accountId } : undefined;
  const categories = useMemo(
    () => Array.from(new Set(products.map((item) => item.category))).sort(),
    [products],
  );
  const gallery = useQuery({
    queryKey: ["display-runtime", "media", accountId ?? "self"],
    queryFn: () => api.get<{ items: MediaItem[] }>("/display/media", query),
    enabled: galleryOpen,
  });
  const revisions = useQuery({
    queryKey: [
      "display-runtime",
      "revisions",
      template.id,
      accountId ?? "self",
    ],
    queryFn: () =>
      api.get<{ items: DisplayTemplateRevision[] }>(
        `/display/templates/${template.id}/revisions`,
        query,
      ),
    enabled: historyOpen,
  });

  const commit = useCallback(
    (
      producer: (current: DisplayTemplateDocument) => DisplayTemplateDocument,
    ) => {
      setDocument((current) => {
        const next = producer(current);
        if (JSON.stringify(next) === JSON.stringify(current)) return current;
        setPast((items) => [...items.slice(-39), cloneDocument(current)]);
        setFuture([]);
        return next;
      });
    },
    [],
  );
  const updateLayer = useCallback(
    (id: string, patch: Partial<DisplayLayer>, record = true) => {
      const update = (current: DisplayTemplateDocument) => ({
        ...current,
        layers: current.layers.map((layer) => {
          if (layer.id !== id) return layer;
          if (layer.kind === "game" && patch.visible === false) {
        toast.error(
          c(
            "Oyun alanı ekran tasarımında görünür kalmalıdır.",
            "The game area must remain visible in the template.",
              ),
            );
            return layer;
          }
          const candidate = { ...layer, ...patch };
          const width = Number.isFinite(candidate.width)
            ? clamp(candidate.width, 4, 100)
            : layer.width;
          const height = Number.isFinite(candidate.height)
            ? clamp(candidate.height, 4, 100)
            : layer.height;
          const x = Number.isFinite(candidate.x)
            ? clamp(candidate.x, 0, 100 - width)
            : layer.x;
          const y = Number.isFinite(candidate.y)
            ? clamp(candidate.y, 0, 100 - height)
            : layer.y;
          return { ...candidate, x, y, width, height };
        }),
      });
      if (record) commit(update);
      else setDocument(update);
    },
    [c, commit, toast],
  );
  const undo = useCallback(() => {
    setPast((items) => {
      const previous = items.at(-1);
      if (!previous) return items;
      setFuture((next) => [cloneDocument(document), ...next.slice(0, 39)]);
      setDocument(cloneDocument(previous));
      return items.slice(0, -1);
    });
  }, [document]);
  const redo = useCallback(() => {
    setFuture((items) => {
      const next = items[0];
      if (!next) return items;
      setPast((previous) => [...previous.slice(-39), cloneDocument(document)]);
      setDocument(cloneDocument(next));
      return items.slice(1);
    });
  }, [document]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches("input, textarea, select")) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") {
        event.preventDefault();
        event.shiftKey ? redo() : undo();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "y") {
        event.preventDefault();
        redo();
        return;
      }
      if (
        !selected ||
        selected.locked ||
        !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)
      )
        return;
      event.preventDefault();
      const step = event.shiftKey ? 5 : 1;
      updateLayer(selected.id, {
        x: clamp(
          selected.x +
            (event.key === "ArrowLeft"
              ? -step
              : event.key === "ArrowRight"
                ? step
                : 0),
          0,
          100 - selected.width,
        ),
        y: clamp(
          selected.y +
            (event.key === "ArrowUp"
              ? -step
              : event.key === "ArrowDown"
                ? step
                : 0),
          0,
          100 - selected.height,
        ),
      });
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [redo, selected, undo, updateLayer]);

  const addLayer = (
    kind: DisplayLayerKind,
    position?: { x: number; y: number },
  ) => {
    if (
      kind === "game" &&
      document.layers.some((layer) => layer.kind === "game")
    ) {
      toast.error(
        c(
          "Bir ekran tasarımında yalnız bir oyun alanı olabilir.",
          "A template can contain only one game area.",
        ),
      );
      return;
    }
    const definition = LAYER_LIBRARY.find((item) => item.kind === kind)!;
    const id = nextLayerId(kind, document.layers);
    const layer: DisplayLayer = {
      id,
      kind,
      x: round(
        clamp(
          (position?.x ?? 50) - definition.width / 2,
          0,
          100 - definition.width,
        ),
        true,
      ),
      y: round(
        clamp(
          (position?.y ?? 50) - definition.height / 2,
          0,
          100 - definition.height,
        ),
        true,
      ),
      width: definition.width,
      height: definition.height,
      zIndex: Math.max(0, ...document.layers.map((item) => item.zIndex)) + 1,
      visible: true,
      locked: false,
      settings:
        kind === "qr"
          ? { errorCorrection: "L", quietZone: 4 }
          : kind === "ad" || kind === "sponsor"
            ? { consentRequired: true, fit: "cover" }
            : {},
    };
    commit((current) => ({ ...current, layers: [...current.layers, layer] }));
    setSelectedId(id);
  };
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handleDrop = (event: DragEvent) => {
      const kind = event.dataTransfer?.getData(
        "application/x-wondeon-layer",
      ) as DisplayLayerKind;
      if (!LAYER_LIBRARY.some((item) => item.kind === kind)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const bounds = canvas.getBoundingClientRect();
      addLayer(kind, {
        x: ((event.clientX - bounds.left) / bounds.width) * 100,
        y: ((event.clientY - bounds.top) / bounds.height) * 100,
      });
    };
    // React'in merkezde ekleyen yedek drop işleyicisinden önce gerçek konumu uygular.
    canvas.addEventListener("drop", handleDrop, { capture: true });
    return () =>
      canvas.removeEventListener("drop", handleDrop, { capture: true });
  });
  const duplicateSelected = () => {
    if (!selected || selected.kind === "game") {
      if (selected?.kind === "game")
        toast.error(
          c("Oyun alanı çoğaltılamaz.", "The game area cannot be duplicated."),
        );
      return;
    }
    const id = nextLayerId(selected.kind, document.layers);
    const copyLayer = {
      ...selected,
      id,
      x: clamp(selected.x + 2, 0, 100 - selected.width),
      y: clamp(selected.y + 2, 0, 100 - selected.height),
      zIndex: Math.max(...document.layers.map((item) => item.zIndex)) + 1,
    };
    commit((current) => ({
      ...current,
      layers: [...current.layers, copyLayer],
    }));
    setSelectedId(id);
  };
  const removeSelected = () => {
    if (
      !selected ||
      (selected.kind === "game" &&
        document.layers.filter((layer) => layer.kind === "game").length === 1)
    ) {
      toast.error(
        c(
          "En az bir oyun alanı ekran tasarımında kalmalıdır.",
          "At least one game area must remain in the template.",
        ),
      );
      return;
    }
    commit((current) => ({
      ...current,
      layers: current.layers.filter((layer) => layer.id !== selected.id),
    }));
    setSelectedId(
      document.layers.find((layer) => layer.id !== selected.id)?.id ?? "",
    );
  };
  const beginInteraction = (
    event: React.PointerEvent,
    layer: DisplayLayer,
    mode: Interaction["mode"],
  ) => {
    event.stopPropagation();
    if (preview || layer.locked) return;
    setSelectedId(layer.id);
    setInteraction({
      mode,
      id: layer.id,
      startX: event.clientX,
      startY: event.clientY,
      origin: { ...layer },
    });
    canvasRef.current?.setPointerCapture(event.pointerId);
  };
  const moveInteraction = (event: React.PointerEvent) => {
    if (!interaction || !canvasRef.current) return;
    const bounds = canvasRef.current.getBoundingClientRect();
    const dx = ((event.clientX - interaction.startX) / bounds.width) * 100;
    const dy = ((event.clientY - interaction.startY) / bounds.height) * 100;
    if (interaction.mode === "move")
      updateLayer(
        interaction.id,
        {
          x: round(
            clamp(interaction.origin.x + dx, 0, 100 - interaction.origin.width),
            snap,
          ),
          y: round(
            clamp(
              interaction.origin.y + dy,
              0,
              100 - interaction.origin.height,
            ),
            snap,
          ),
        },
        false,
      );
    else
      updateLayer(
        interaction.id,
        {
          width: round(
            clamp(interaction.origin.width + dx, 4, 100 - interaction.origin.x),
            snap,
          ),
          height: round(
            clamp(
              interaction.origin.height + dy,
              4,
              100 - interaction.origin.y,
            ),
            snap,
          ),
        },
        false,
      );
  };
  const endInteraction = () => {
    if (!interaction) return;
    const changed = document.layers.find(
      (layer) => layer.id === interaction.id,
    );
    if (
      changed &&
      JSON.stringify(changed) !== JSON.stringify(interaction.origin)
    ) {
      setPast((items) => [
        ...items.slice(-39),
        {
          ...cloneDocument(document),
          layers: document.layers.map((layer) =>
            layer.id === interaction.id ? interaction.origin : layer,
          ),
        },
      ]);
      setFuture([]);
    }
    setInteraction(null);
  };

  const save = useMutation({
    mutationFn: (status?: "draft" | "published") =>
      api.patch(
        `/display/templates/${template.id}`,
        { document, ...(status ? { status } : {}) },
        query,
      ),
    onSuccess: () => {
      toast.success(
        c(
          "Ekran tasarımı geri alınabilir yeni bir sürüm olarak kaydedildi.",
          "Screen design saved as a new rollback-safe revision.",
        ),
      );
      onSaved();
    },
    onError: (error) =>
      toast.error(
        message(
          error,
          c(
            "Ekran tasarımı kaydedilemedi.",
            "Display design could not be saved.",
          ),
        ),
      ),
  });
  const rollback = useMutation({
    mutationFn: (revision: number) =>
      api.post(
        `/display/templates/${template.id}/rollback`,
        { revision },
        query,
      ),
    onSuccess: () => {
      toast.success(
        c(
          "Seçilen revizyondan yeni bir taslak oluşturuldu.",
          "A new draft was created from the selected revision.",
        ),
      );
      setHistoryOpen(false);
      onSaved();
    },
    onError: (error) =>
      toast.error(
        message(
          error,
          c("Revizyon geri alınamadı.", "Revision could not be restored."),
        ),
      ),
  });
  const saveBindings = useMutation({
    mutationFn: (
      bindings: Array<{
        targetType: DisplayTemplateBindingTarget;
        targetKey: string;
      }>,
    ) =>
      api.put(
        `/display/templates/${template.id}/bindings`,
        { bindings },
        query,
      ),
    onSuccess: () => {
      toast.success(c("Kullanım kuralı kaydedildi.", "Usage rule saved."));
      onSaved();
    },
    onError: (error) =>
      toast.error(
        message(
          error,
          c("Kullanım kuralı kaydedilemedi.", "Usage rule could not be saved."),
        ),
      ),
  });

  const bindingOptions =
    bindingType === "account"
      ? [
          {
            value: template.accountId,
            label: c("Bu müşteri hesabı", "This customer account"),
          },
        ]
      : bindingType === "catalog"
        ? categories.map((value) => ({ value, label: value }))
        : bindingType === "product"
          ? products.map((item) => ({ value: item.id, label: item.name }))
          : endpoints.map((item) => ({ value: item.id, label: item.name }));
  useEffect(() => {
    if (!bindingOptions.some((option) => option.value === bindingKey))
      setBindingKey(bindingOptions[0]?.value ?? "");
  }, [bindingKey, bindingOptions]);

  return (
    <section
      className="template-editor"
      aria-label={c(
        "No-code ekran tasarımı editörü",
        "No-code screen design editor",
      )}
    >
      <header className="template-editor__topbar">
        <div>
          <Button size="sm" variant="ghost" onClick={onClose}>
            {c("Tüm tasarımlar", "All designs")}
          </Button>
          <h3>{template.name}</h3>
          <p>
            {c(
              "Bileşen ekleyin, tuvalde konumlandırın ve yayınlanacak görünümü önizleyin.",
              "Add components, position them on the canvas, and preview the broadcast output.",
            )}
          </p>
        </div>
        <div className="template-editor__top-actions">
          <Badge
            tone={
              hasUnpublishedRevision
                ? "warning"
                : template.status === "published"
                  ? "success"
                  : "neutral"
            }
          >
            {hasUnpublishedRevision
              ? c("Yayınlanmamış değişiklik", "Unpublished changes")
              : template.status === "published"
                ? c("Yayında", "Published")
                : c("Taslak", "Draft")}{" "}
            · r{template.revision}
          </Badge>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setAssignmentOpen(true)}
          >
            {c("Nerede kullanılacak?", "Where will it be used?")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setHistoryOpen(true)}
          >
            {c("Yayın geçmişi", "Revision history")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => save.mutate("draft")}
            loading={save.isPending}
          >
            {c("Taslağı kaydet", "Save draft")}
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => save.mutate("published")}
            loading={save.isPending}
          >
            {c("Kaydet ve yayınla", "Save and publish")}
          </Button>
        </div>
      </header>
      <div className="template-editor__workspace">
        <aside
          className="template-editor__library"
          aria-label={c("Bileşenler", "Components")}
        >
          <div className="template-editor__panel-title">
            <div>
              <strong>{c("Bileşenler", "Components")}</strong>
            </div>
            <small>
              {c(
                "Tıklayın veya tuvale sürükleyin",
                "Click or drag onto canvas",
              )}
            </small>
          </div>
          <div className="template-editor__tools">
            {LAYER_LIBRARY.map((item) => (
              <button
                type="button"
                key={item.kind}
                draggable
                onDragStart={(event) =>
                  event.dataTransfer.setData(
                    "application/x-wondeon-layer",
                    item.kind,
                  )
                }
                onClick={() => addLayer(item.kind)}
              >
                <i aria-hidden="true">{item.kind.slice(0, 2).toUpperCase()}</i>
                <span>{c(item.tr, item.en)}</span>
                <b aria-hidden="true">+</b>
              </button>
            ))}
          </div>
        </aside>
        <main className="template-editor__main">
          <div className="template-editor__toolbar">
            <div
              className="template-editor__presets"
              role="group"
              aria-label={c("Ekran boyutu", "Screen size")}
            >
              {PRESETS.map((preset) => (
                <button
                  type="button"
                  key={preset.id}
                  className={
                    document.viewport.width === preset.width &&
                    document.viewport.height === preset.height
                      ? "is-active"
                      : ""
                  }
                  onClick={() =>
                    commit((current) => ({
                      ...current,
                      viewport: {
                        ...current.viewport,
                        width: preset.width,
                        height: preset.height,
                      },
                    }))
                  }
                >
                  {c(preset.tr, preset.en)}
                </button>
              ))}
            </div>
            <div className="template-editor__view-actions">
              <Button
                size="sm"
                variant="ghost"
                disabled={!past.length}
                onClick={undo}
                aria-label={c("Geri al", "Undo")}
              >
                {c("Geri al", "Undo")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={!future.length}
                onClick={redo}
                aria-label={c("Yinele", "Redo")}
              >
                {c("Yinele", "Redo")}
              </Button>
              <Checkbox
                checked={showGrid}
                onChange={(event) => setShowGrid(event.target.checked)}
                label={c("Izgara", "Grid")}
              />
              <Checkbox
                checked={snap}
                onChange={(event) => setSnap(event.target.checked)}
                label={c("Hizala", "Snap")}
              />
              <Button
                size="sm"
                variant={preview ? "primary" : "ghost"}
                onClick={() => setPreview((value) => !value)}
              >
                {preview
                  ? c("Düzenlemeye dön", "Return to edit")
                  : c("Önizle", "Preview")}
              </Button>
            </div>
          </div>
          <div className="template-editor__stage">
            <div className="template-editor__ruler">
              <span>
                {document.viewport.width} × {document.viewport.height}
              </span>
              <small>
                {c("Güvenli alan", "Safe area")} %
                {document.viewport.safeAreaPercent}
              </small>
            </div>
            <div
              ref={canvasRef}
              className={`template-editor__canvas${showGrid ? " has-grid" : ""}${preview ? " is-preview" : ""}`}
              style={
                {
                  aspectRatio: `${document.viewport.width} / ${document.viewport.height}`,
                  "--template-bg": document.theme.background,
                  "--template-fg": document.theme.foreground,
                  "--template-accent": document.theme.accent,
                  "--safe-area": `${document.viewport.safeAreaPercent}%`,
                  "--template-radius": `${document.theme.radius}px`,
                  "--viewport-ratio":
                    document.viewport.width / document.viewport.height,
                } as React.CSSProperties
              }
              onPointerMove={moveInteraction}
              onPointerUp={endInteraction}
              onPointerCancel={endInteraction}
              onPointerDown={(event) => {
                if (event.target === event.currentTarget) setSelectedId("");
              }}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                const kind = event.dataTransfer.getData(
                  "application/x-wondeon-layer",
                ) as DisplayLayerKind;
                if (LAYER_LIBRARY.some((item) => item.kind === kind))
                  addLayer(kind);
              }}
            >
              <div className="template-editor__safe-area" aria-hidden="true" />
              {document.layers
                .slice()
                .sort((left, right) => left.zIndex - right.zIndex)
                .map((layer) =>
                  layer.visible ? (
                    <div
                      key={layer.id}
                      role="button"
                      tabIndex={preview ? -1 : 0}
                      aria-label={`${layerLabel(layer.kind, c)} · ${layer.id}`}
                      aria-pressed={selectedId === layer.id}
                      className={`template-editor__layer template-editor__layer--${layer.kind}${selectedId === layer.id ? " is-selected" : ""}${layer.locked ? " is-locked" : ""}`}
                      style={{
                        left: `${layer.x}%`,
                        top: `${layer.y}%`,
                        width: `${layer.width}%`,
                        height: `${layer.height}%`,
                        zIndex: layer.zIndex,
                        opacity: Number(
                          (layer.settings as Record<string, unknown>).opacity ??
                            1,
                        ),
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!preview) setSelectedId(layer.id);
                      }}
                      onPointerDown={(event) =>
                        beginInteraction(event, layer, "move")
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedId(layer.id);
                        }
                      }}
                    >
                      {sampleForLayer(layer, c)}
                      {!preview && selectedId === layer.id && !layer.locked ? (
                        <button
                          type="button"
                          className="template-editor__resize"
                          onPointerDown={(event) =>
                            beginInteraction(event, layer, "resize")
                          }
                          aria-label={c(
                            "Katmanı yeniden boyutlandır",
                            "Resize layer",
                          )}
                        />
                      ) : null}
                    </div>
                  ) : null,
                )}
            </div>
          </div>
          <p className="template-editor__keyboard-hint">
            {c(
              "İpucu: Ok tuşları 1%, Shift + ok 5% taşır. Ctrl/Cmd + Z geri alır.",
              "Tip: Arrow keys move 1%, Shift + arrow 5%. Ctrl/Cmd + Z undoes.",
            )}
          </p>
        </main>
        <aside
          className="template-editor__inspector"
          aria-label={c("Katman denetçisi", "Layer inspector")}
        >
          <div className="template-editor__panel-title">
            <div>
              <strong>{c("Katmanlar", "Layers")}</strong>
            </div>
            <small>
              {document.layers.length} {c("bileşen", "components")}
            </small>
          </div>
          <div className="template-editor__layer-list">
            {document.layers
              .slice()
              .sort((left, right) => right.zIndex - left.zIndex)
              .map((layer) => (
                <button
                  type="button"
                  key={layer.id}
                  className={selectedId === layer.id ? "is-active" : ""}
                  onClick={() => setSelectedId(layer.id)}
                >
                  <i>{layer.kind.slice(0, 2).toUpperCase()}</i>
                  <span>
                    <strong>{layerLabel(layer.kind, c)}</strong>
                    <small>{layer.id}</small>
                  </span>
                  <em>
                    {layer.visible
                      ? layer.locked
                        ? c("Kilitli", "Locked")
                        : c("Açık", "Shown")
                      : c("Gizli", "Hidden")}
                  </em>
                </button>
              ))}
          </div>
          {selected ? (
            <div className="template-editor__properties">
              <div className="template-editor__property-head">
                <div>
                  <small>{c("SEÇİLİ KATMAN", "SELECTED LAYER")}</small>
                  <strong>{layerLabel(selected.kind, c)}</strong>
                </div>
                <Badge tone={selected.visible ? "success" : "neutral"}>
                  {selected.visible
                    ? c("Görünür", "Visible")
                    : c("Gizli", "Hidden")}
                </Badge>
              </div>
              <div className="template-editor__property-actions">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    updateLayer(selected.id, { visible: !selected.visible })
                  }
                >
                  {selected.visible ? c("Gizle", "Hide") : c("Göster", "Show")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    updateLayer(selected.id, { locked: !selected.locked })
                  }
                >
                  {selected.locked
                    ? c("Kilidi aç", "Unlock")
                    : c("Kilitle", "Lock")}
                </Button>
                <Button size="sm" variant="ghost" onClick={duplicateSelected}>
                  {c("Çoğalt", "Duplicate")}
                </Button>
                <Button size="sm" variant="danger" onClick={removeSelected}>
                  {c("Sil", "Delete")}
                </Button>
              </div>
              <details className="template-editor__settings-group" open>
                <summary>{c("Konum ve boyut", "Position and size")}</summary>
                <div className="template-editor__geometry">
                  {(["x", "y", "width", "height"] as const).map((field) => (
                    <Field
                      key={field}
                      label={
                        field === "width"
                          ? c("Genişlik", "Width")
                          : field === "height"
                            ? c("Yükseklik", "Height")
                            : field.toUpperCase()
                      }
                    >
                      {({ id }) => (
                        <Input
                          id={id}
                          type="number"
                          min={field === "width" || field === "height" ? 4 : 0}
                          max={100}
                          value={selected[field]}
                          disabled={selected.locked}
                          onChange={(event) =>
                            updateLayer(selected.id, {
                              [field]: Number(event.target.value),
                            })
                          }
                        />
                      )}
                    </Field>
                  ))}
                </div>
                <div className="template-editor__z-actions">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      updateLayer(selected.id, {
                        zIndex: clamp(selected.zIndex + 1, 0, 1000),
                      })
                    }
                  >
                    {c("Öne getir", "Bring forward")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      updateLayer(selected.id, {
                        zIndex: clamp(selected.zIndex - 1, 0, 1000),
                      })
                    }
                  >
                    {c("Arkaya gönder", "Send backward")}
                  </Button>
                </div>
              </details>
              <details className="template-editor__settings-group" open>
                <summary>{c("İçerik", "Content")}</summary>
                <div className="template-editor__settings-body">
                  <Field label={c("Etiket", "Label")}>
                    {({ id }) => (
                      <Input
                        id={id}
                        value={String(selected.settings.label ?? "")}
                        onChange={(event) =>
                          updateLayer(selected.id, {
                            settings: {
                              ...selected.settings,
                              label: event.target.value,
                            },
                          })
                        }
                      />
                    )}
                  </Field>
                  {["logo", "ad", "sponsor"].includes(selected.kind) ? (
                    <>
                      <Field label={c("Medya adresi", "Media URL")}>
                        {({ id }) => (
                          <Input
                            id={id}
                            value={String(selected.settings.mediaUrl ?? "")}
                            placeholder="https://…"
                            onChange={(event) =>
                              updateLayer(selected.id, {
                                settings: {
                                  ...selected.settings,
                                  mediaUrl: event.target.value,
                                },
                              })
                            }
                          />
                        )}
                      </Field>
                      <Button size="sm" onClick={() => setGalleryOpen(true)}>
                        {c("Hub galerisinden seç", "Choose from Hub gallery")}
                      </Button>
                    </>
                  ) : null}
                  {["score", "timer"].includes(selected.kind) ? (
                    <Field label={c("Örnek değer", "Sample value")}>
                      {({ id }) => (
                        <Input
                          id={id}
                          value={String(selected.settings.sampleValue ?? "")}
                          onChange={(event) =>
                            updateLayer(selected.id, {
                              settings: {
                                ...selected.settings,
                                sampleValue: event.target.value,
                              },
                            })
                          }
                        />
                      )}
                    </Field>
                  ) : null}
                </div>
              </details>
            </div>
          ) : (
            <div className="template-editor__empty-inspector">
              <strong>{c("Bir katman seçin", "Select a layer")}</strong>
              <span>
                {c(
                  "Tuvalde veya katman listesinde bir bileşene tıklayın.",
                  "Choose a component on the canvas or in the layer list.",
                )}
              </span>
            </div>
          )}
          <details className="template-editor__theme-controls">
            <summary>{c("Ekran görünümü", "Screen appearance")}</summary>
            <div>
              <Field label={c("Vurgu", "Accent")}>
                {({ id }) => (
                  <Input
                    id={id}
                    type="color"
                    value={document.theme.accent}
                    onChange={(event) =>
                      commit((current) => ({
                        ...current,
                        theme: { ...current.theme, accent: event.target.value },
                      }))
                    }
                  />
                )}
              </Field>
              <Field label={c("Arka plan", "Background")}>
                {({ id }) => (
                  <Input
                    id={id}
                    type="color"
                    value={document.theme.background}
                    onChange={(event) =>
                      commit((current) => ({
                        ...current,
                        theme: {
                          ...current.theme,
                          background: event.target.value,
                        },
                      }))
                    }
                  />
                )}
              </Field>
              <Field label={c("Güvenli kenar", "Safe area")}>
                {({ id }) => (
                  <Input
                    id={id}
                    type="number"
                    min={0}
                    max={20}
                    value={document.viewport.safeAreaPercent}
                    onChange={(event) =>
                      commit((current) => ({
                        ...current,
                        viewport: {
                          ...current.viewport,
                          safeAreaPercent: Number(event.target.value),
                        },
                      }))
                    }
                  />
                )}
              </Field>
            </div>
          </details>
        </aside>
      </div>
      <Modal
        open={assignmentOpen}
        onClose={() => setAssignmentOpen(false)}
        title={c(
          "Bu tasarım nerede kullanılsın?",
          "Where should this design be used?",
        )}
        description={c(
          "Bu gelişmiş kuralı yalnız belirli bir müşteri, katalog, ürün veya cihaz için farklı ekran gerektiğinde ekleyin.",
          "Add this advanced rule only when a specific account, catalog, product, or device needs a different screen.",
        )}
        size="lg"
      >
        <div className="template-editor__assignment-modal">
          <div className="template-editor__assignment-form">
            <Field label={c("Kullanım alanı", "Usage scope")}>
              {({ id }) => (
                <Select
                  id={id}
                  value={bindingType}
                  onChange={(event) =>
                    setBindingType(
                      event.target.value as DisplayTemplateBindingTarget,
                    )
                  }
                >
                  <option value="account">{c("Müşteri", "Account")}</option>
                  <option value="catalog">{c("Katalog", "Catalog")}</option>
                  <option value="product">{c("Ürün", "Product")}</option>
                  <option value="endpoint">{c("Cihaz", "Device")}</option>
                </Select>
              )}
            </Field>
            <Field label={c("Seçim", "Selection")}>
              {({ id }) => (
                <Select
                  id={id}
                  value={bindingKey}
                  onChange={(event) => setBindingKey(event.target.value)}
                >
                  <option value="">{c("Seçin", "Choose")}</option>
                  {bindingOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Button
              variant="primary"
              disabled={!bindingKey}
              loading={saveBindings.isPending}
              onClick={() =>
                saveBindings.mutate(
                  [
                    ...template.bindings.map(({ targetType, targetKey }) => ({
                      targetType,
                      targetKey,
                    })),
                    { targetType: bindingType, targetKey: bindingKey },
                  ].filter(
                    (binding, index, all) =>
                      all.findIndex(
                        (candidate) =>
                          candidate.targetType === binding.targetType &&
                          candidate.targetKey === binding.targetKey,
                      ) === index,
                  ),
                )
              }
            >
              {c("Kullanım kuralı ekle", "Add usage rule")}
            </Button>
          </div>
          <div className="template-editor__assignment-list">
            <strong>
              {c("Etkin kullanım kuralları", "Active usage rules")}
            </strong>
            {template.bindings.length ? (
              <ul>
                {template.bindings.map((binding) => (
                  <li key={binding.id}>
                    <span>
                      <small>
                        {binding.targetType === "account"
                          ? c("Müşteri", "Account")
                          : binding.targetType === "catalog"
                            ? c("Katalog", "Catalog")
                            : binding.targetType === "product"
                              ? c("Ürün", "Product")
                              : c("Cihaz", "Device")}
                      </small>
                      <b>{binding.label ?? binding.targetKey}</b>
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        saveBindings.mutate(
                          template.bindings
                            .filter((item) => item.id !== binding.id)
                            .map(({ targetType, targetKey }) => ({
                              targetType,
                              targetKey,
                            })),
                        )
                      }
                    >
                      {c("Kaldır", "Remove")}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>
                {c(
                  "Özel bir kural yok. Bu tasarım henüz belirli bir hedefe bağlanmadı.",
                  "There is no special rule. This design is not assigned to a specific target yet.",
                )}
              </p>
            )}
          </div>
          <p className="template-editor__assignment-note">
            {c(
              "Birden fazla kural eşleşirse cihaz en yüksek, müşteri en düşük önceliğe sahiptir.",
              "When multiple rules match, device has the highest priority and account has the lowest.",
            )}
          </p>
        </div>
      </Modal>
      <Modal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        title={c("Hub galerisi", "Hub gallery")}
        description={c(
          "Yalnız güvenlik kontrolünü geçmiş, yayına hazır medya gösterilir.",
          "Only publication-ready media that passed security checks is shown.",
        )}
        size="lg"
      >
        {gallery.isLoading ? (
          <p>{c("Galeri yükleniyor…", "Loading gallery…")}</p>
        ) : gallery.data?.items.filter(
            (item) => selected?.kind !== "logo" || item.kind === "image",
          ).length ? (
          <div className="template-editor__media-grid">
            {gallery.data.items
              .filter(
                (item) => selected?.kind !== "logo" || item.kind === "image",
              )
              .map((item) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => {
                    if (selected)
                      updateLayer(selected.id, {
                        settings: {
                          ...selected.settings,
                          mediaUrl: item.url,
                          mediaKind: item.kind === "video" ? "video" : "image",
                          ...(item.posterUrl
                            ? { posterUrl: item.posterUrl }
                            : {}),
                        },
                      });
                    setGalleryOpen(false);
                  }}
                >
                  <span>
                    {item.kind === "image" ? (
                      <img src={item.url} alt="" />
                    ) : item.posterUrl ? (
                      <img src={item.posterUrl} alt="" />
                    ) : (
                      <i>VIDEO</i>
                    )}
                  </span>
                  <strong>{item.name}</strong>
                </button>
              ))}
          </div>
        ) : (
          <p>
            {c(
              "Bu bileşen için yayına hazır medya bulunmuyor.",
              "No publication-ready media is available for this component.",
            )}
          </p>
        )}
      </Modal>
      <Modal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={c(
          "Yayın ve revizyon geçmişi",
          "Publication and revision history",
        )}
        description={c(
          "Eski bir sürümü seçmek onu silmez; yeni bir taslak revizyon oluşturur.",
          "Restoring an older version never deletes history; it creates a new draft revision.",
        )}
        size="lg"
      >
        {revisions.isLoading ? (
          <p>{c("Revizyonlar yükleniyor…", "Loading revisions…")}</p>
        ) : (
          <div className="template-editor__revision-list">
            {(revisions.data?.items ?? []).map((revision) => (
              <article key={revision.id}>
                <div>
                  <Badge
                    tone={
                      template.publishedRevision === revision.revision
                        ? "success"
                        : "neutral"
                    }
                  >
                    r{revision.revision}
                    {template.publishedRevision === revision.revision
                      ? ` · ${c("Canlı", "Live")}`
                      : ""}
                  </Badge>
                  <strong>{revision.name}</strong>
                  <small>
                    {new Date(revision.createdAt).toLocaleString(
                      language === "tr" ? "tr-TR" : "en-US",
                    )}
                  </small>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={revision.revision === template.revision}
                  loading={rollback.isPending}
                  onClick={() => rollback.mutate(revision.revision)}
                >
                  {c(
                    "Bu sürümden taslak oluştur",
                    "Create draft from this version",
                  )}
                </Button>
              </article>
            ))}
          </div>
        )}
      </Modal>
    </section>
  );
}

export function GameVariantEditor({
  variant,
  accountId,
  audience,
  areaScenes = [],
  onSaved,
}: {
  variant: GameVariantItem;
  accountId?: string;
  audience: "admin" | "customer";
  areaScenes?: Array<{ id: string; name: string; coreProductId: string; status: string }>;
  onSaved: () => void;
}) {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const toast = useToast();
  const [displayName, setDisplayName] = useState(
    String(variant.branding.displayName ?? ""),
  );
  const [logoUrl, setLogoUrl] = useState(
    String(variant.branding.logoUrl ?? ""),
  );
  const [accentColor, setAccentColor] = useState(
    String(variant.branding.accentColor ?? "#6658e8"),
  );
  const [bannerAssetUrl, setBannerAssetUrl] = useState(
    String(variant.advertising.bannerAssetUrl ?? variant.advertising.assetUrl ?? ""),
  );
  const [sponsorLogoUrl, setSponsorLogoUrl] = useState(
    String(variant.advertising.sponsorLogoUrl ?? ""),
  );
  const [consentGranted, setConsentGranted] = useState(
    variant.advertising.consentGranted === true,
  );
  const [approved, setApproved] = useState(
    variant.advertising.approved === true,
  );
  const [areaSceneId, setAreaSceneId] = useState(variant.areaSceneId ?? "");
  const [gameSettings, setGameSettings] = useState<Record<string, unknown>>(
    () => ({ ...variant.gameSettings }),
  );
  const media = useQuery({
    queryKey: ["display-runtime", "variant-media", accountId ?? "self"],
    queryFn: () =>
      api.get<{ items: MediaItem[] }>(
        "/display/media",
        accountId ? { accountId } : undefined,
      ),
  });
  useEffect(() => {
    setDisplayName(String(variant.branding.displayName ?? ""));
    setLogoUrl(String(variant.branding.logoUrl ?? ""));
    setAccentColor(String(variant.branding.accentColor ?? "#6658e8"));
    setBannerAssetUrl(String(variant.advertising.bannerAssetUrl ?? variant.advertising.assetUrl ?? ""));
    setSponsorLogoUrl(String(variant.advertising.sponsorLogoUrl ?? ""));
    setConsentGranted(variant.advertising.consentGranted === true);
    setApproved(variant.advertising.approved === true);
    setAreaSceneId(variant.areaSceneId ?? "");
    setGameSettings({ ...variant.gameSettings });
  }, [variant]);
  const labelFor = (definition: DisplayGameSettingDefinition) =>
    (language === "tr" ? definition.label.tr : definition.label.en) ??
    definition.label.tr ??
    definition.label.en ??
    definition.key;
  const helpFor = (definition: DisplayGameSettingDefinition) =>
    language === "tr" ? definition.help?.tr : definition.help?.en;
  const updateGameSetting = (key: string, value: unknown) =>
    setGameSettings((current) => ({ ...current, [key]: value }));
  const save = useMutation({
    mutationFn: () =>
      api.patch(
        `/display/variants/${variant.id}`,
        {
          areaSceneId: areaSceneId || null,
          branding: {
            ...(displayName ? { displayName } : {}),
            ...(logoUrl ? { logoUrl } : {}),
            accentColor,
          },
          advertising: {
            ...(bannerAssetUrl ? { bannerAssetUrl } : {}),
            ...(sponsorLogoUrl ? { sponsorLogoUrl } : {}),
            consentRequired: true,
            consentGranted,
            ...(audience === "admin" ? { approved } : {}),
          },
          gameSettings,
        },
        accountId ? { accountId } : undefined,
      ),
    onSuccess: () => {
      toast.success(
        c("Oyun özelleştirmesi kaydedildi.", "Game customization saved."),
      );
      onSaved();
    },
    onError: (error) =>
      toast.error(
        message(
          error,
          c(
            "Oyun özelleştirmesi kaydedilemedi.",
            "Game customization could not be saved.",
          ),
        ),
      ),
  });
  return (
    <Card
      title={`${c("Oyunu özelleştir", "Customize game")} · ${variant.name}`}
      action={
        <Button
          size="sm"
          variant="primary"
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          {c("Revizyonu kaydet", "Save revision")}
        </Button>
      }
    >
      <div className="display-editor__theme">
        <Field label={c("Etkinlikte görünen ad", "Display name")}>
          {({ id }) => (
            <Input
              id={id}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          )}
        </Field>
        <Field label={c("Logo", "Logo")}>
          {({ id }) => (
            <Select
              id={id}
              value={logoUrl}
              onChange={(event) => setLogoUrl(event.target.value)}
            >
              <option value="">{c("Logo kullanma", "No logo")}</option>
              {(media.data?.items ?? [])
                .filter((item) => item.kind === "image")
                .map((item) => (
                  <option key={item.id} value={item.url}>
                    {item.name}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        <Field label={c("Vurgu rengi", "Accent color")}>
          {({ id }) => (
            <Input
              id={id}
              type="color"
              value={accentColor}
              onChange={(event) => setAccentColor(event.target.value)}
            />
          )}
        </Field>
        <Field label={c("3B oyun alanı", "3D game area")}>
          {({ id }) => (
            <Select
              id={id}
              value={areaSceneId}
              onChange={(event) => setAreaSceneId(event.target.value)}
            >
              <option value="">{c("Varsayılan oyun alanı", "Default game area")}</option>
              {areaScenes
                .filter(
                  (scene) =>
                    scene.coreProductId === variant.coreProductId &&
                    scene.status === "published",
                )
                .map((scene) => (
                  <option key={scene.id} value={scene.id}>
                    {scene.name}
                  </option>
                ))}
            </Select>
          )}
        </Field>
      </div>
      {variant.coreProductMedia?.bannerUrl ||
      variant.coreProductMedia?.coverUrl ? (
        <div className="display-editor__catalog-preview">
          <img
            src={
              variant.coreProductMedia.bannerUrl ??
              variant.coreProductMedia.coverUrl ??
              ""
            }
            alt=""
          />
          <div>
            <strong>{c("Katalogdan canlı görsel", "Live catalog artwork")}</strong>
            <small>
              {c(
                "Hub kataloğundaki görsel değiştiğinde bu oyun yüzeyine de yeni sürüm gelir.",
                "When the Hub catalog artwork changes, the new revision reaches this game surface too.",
              )}
            </small>
          </div>
        </div>
      ) : null}
      <section className="display-editor__game-settings">
        <header>
          <div>
            <strong>{c("Oyuna özel güvenli ayarlar", "Safe game settings")}</strong>
            <small>
              {c(
                "Yalnızca oyunun yayınladığı ve sunucunun doğruladığı seçenekler gösterilir.",
                "Only options published by the game and validated by the server are shown.",
              )}
            </small>
          </div>
          <Badge tone="neutral">
            {variant.gameSettingDefinitions?.length ?? 0} {c("ayar", "settings")}
          </Badge>
        </header>
        {variant.gameSettingDefinitions?.length ? (
          <div className="display-editor__settings-grid">
            {variant.gameSettingDefinitions.map((definition) => {
              const value =
                gameSettings[definition.key] ?? definition.defaultValue ?? "";
              if (definition.kind === "boolean") {
                return (
                  <Checkbox
                    key={definition.key}
                    checked={value === true}
                    onChange={(event) =>
                      updateGameSetting(definition.key, event.target.checked)
                    }
                    label={labelFor(definition)}
                  />
                );
              }
              return (
                <Field
                  key={definition.key}
                  label={labelFor(definition)}
                  hint={helpFor(definition)}
                >
                  {({ id }) =>
                    definition.kind === "select" ? (
                      <Select
                        id={id}
                        value={String(value)}
                        onChange={(event) =>
                          updateGameSetting(definition.key, event.target.value)
                        }
                      >
                        {(definition.options ?? []).map((option) => (
                          <option key={option.value} value={option.value}>
                            {language === "tr" ? option.label.tr : option.label.en}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <Input
                        id={id}
                        type={
                          definition.kind === "number"
                            ? "number"
                            : definition.kind === "color"
                              ? "color"
                              : "text"
                        }
                        value={String(value)}
                        min={definition.min}
                        max={definition.max}
                        step={definition.step}
                        onChange={(event) =>
                          updateGameSetting(
                            definition.key,
                            definition.kind === "number"
                              ? Number(event.target.value)
                              : event.target.value,
                          )
                        }
                      />
                    )
                  }
                </Field>
              );
            })}
          </div>
        ) : (
          <p className="display-editor__empty-settings">
            {c(
              "Bu ana oyun özelleştirilebilir ayar yayınlamıyor. Marka, ekran tasarımı ve sponsor alanları yine değiştirilebilir.",
              "This core game does not publish customizable settings. Brand, screen design, and sponsor areas can still be changed.",
            )}
          </p>
        )}
      </section>
      <div className="display-editor__sponsor">
        <Field label={c("Reklam bandı", "Advertising banner")}>
          {({ id }) => (
            <Select
              id={id}
              value={bannerAssetUrl}
              onChange={(event) => setBannerAssetUrl(event.target.value)}
            >
              <option value="">{c("Reklam bandı kullanma", "No advertising banner")}</option>
              {(media.data?.items ?? []).map((item) => (
                <option key={item.id} value={item.url}>
                  {item.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={c("Sponsor logosu", "Sponsor logo")}>
          {({ id }) => (
            <Select
              id={id}
              value={sponsorLogoUrl}
              onChange={(event) => setSponsorLogoUrl(event.target.value)}
            >
              <option value="">{c("Sponsor logosu kullanma", "No sponsor logo")}</option>
              {(media.data?.items ?? [])
                .filter((item) => item.kind === "image")
                .map((item) => (
                  <option key={item.id} value={item.url}>
                    {item.name}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        <Checkbox
          checked={consentGranted}
          onChange={(event) => setConsentGranted(event.target.checked)}
          label={c(
            "Müşteri açık rızası kaydedildi",
            "Customer consent recorded",
          )}
        />
        {audience === "admin" ? (
          <Checkbox
            checked={approved}
            onChange={(event) => setApproved(event.target.checked)}
            label={c("Wondeon içerik onayı", "Wondeon content approval")}
          />
        ) : null}
      </div>
    </Card>
  );
}
